import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * Rule-set create + edit modal — same form, one component.
 *
 * In edit mode the rules list is authoritative: saving replaces the set's
 * existing rules atomically on the server. The visual rule builder
 * handles the composition of rules (ranges, comparisons, divisibility,
 * advanced free-text), serialises each row to the server's expression
 * grammar, and maps server-side `line_errors` back onto the matching
 * row for inline highlighting.
 *
 * Prefill approach: `initialValues` on a fresh Form each open plus a
 * key that changes with initial.id / mode guarantees a clean mount per
 * target set with no state leaking between opens.
 */
import React, { useEffect, useState } from 'react';
import { Alert, Checkbox, Form, Input, Modal, Typography } from 'antd';
import { createRuleSet, updateRuleSet, } from '../../api';
import { ApiError } from '../../api/client';
import { notifySuccess } from '../errors';
import { parseExpression, RuleBuilder, serializeRow, } from './ruleBuilder';
const { Text } = Typography;
export function RuleSetModal({ open, onClose, onDone, templateId, initial, variables }) {
    const [form] = Form.useForm();
    const [submitting, setSubmitting] = useState(false);
    const isEdit = !!initial;
    const [rows, setRows] = useState([]);
    const [serverErrors, setServerErrors] = useState({});
    const [showUnsatisfiableWarning, setShowUnsatisfiableWarning] = useState(false);
    const [lastSavedId, setLastSavedId] = useState(null);
    // Reset rows + errors when the modal opens or switches between targets.
    useEffect(() => {
        if (!open)
            return;
        setServerErrors({});
        if (initial) {
            setRows(initial.rules.map((r) => parseExpression(r.rule_expression)));
        }
        else {
            setRows([]);
        }
    }, [open, initial]);
    const initialValues = initial
        ? { name: initial.name, is_default: initial.is_default }
        : undefined;
    const doSave = async (force) => {
        let v;
        try {
            v = await form.validateFields();
        }
        catch {
            return 'other';
        }
        const rules = rows
            .map((row) => serializeRow(row).trim())
            .filter((s) => s.length > 0)
            .map((expression) => ({ expression }));
        try {
            if (isEdit) {
                const r = await updateRuleSet(initial.id, {
                    name: v.name.trim(),
                    is_default: !!v.is_default,
                    rules,
                    force,
                });
                notifySuccess(r.rules_replaced !== null
                    ? `הסט עודכן (${r.rules_replaced} חוקים)`
                    : 'הסט עודכן');
                setServerErrors({});
                setLastSavedId(initial.id);
                form.resetFields();
                onDone(initial.id);
                return 'ok';
            }
            else {
                const r = await createRuleSet({
                    question_template_id: templateId,
                    name: v.name.trim(),
                    is_default: !!v.is_default,
                    rules: rules.length > 0 ? rules : undefined,
                    force,
                });
                notifySuccess(rules.length > 0 ? `סט נוצר עם ${r.rules_added} חוקים` : 'סט חוקים נוצר');
                setServerErrors({});
                setLastSavedId(r.id);
                form.resetFields();
                onDone(r.id);
                return 'ok';
            }
        }
        catch (err) {
            if (err instanceof ApiError) {
                if (err.code === 'rule_syntax') {
                    const errs = err.details.line_errors ?? [];
                    // The server returns errors indexed by position in the array
                    // we just sent; map them back onto the matching builder row.
                    const map = {};
                    for (const e of errs)
                        map[e.index] = `${e.expression}: ${e.message}`;
                    setServerErrors(map);
                    return 'syntax';
                }
                if (err.code === 'unsatisfiable_rules')
                    return 'unsatisfiable';
            }
            return 'other';
        }
    };
    const submit = async () => {
        setSubmitting(true);
        try {
            const result = await doSave(false);
            if (result === 'unsatisfiable')
                setShowUnsatisfiableWarning(true);
        }
        finally {
            setSubmitting(false);
        }
    };
    const confirmForceSave = async () => {
        setShowUnsatisfiableWarning(false);
        setSubmitting(true);
        try {
            await doSave(true);
        }
        finally {
            setSubmitting(false);
        }
    };
    const nameLooksLikeRule = (_, value) => {
        if (!value)
            return Promise.resolve();
        if (/[<>=!]=?|&&|\|\|/.test(value)) {
            return Promise.reject(new Error('השם הוא תווית (למשל "בסיסי"). ביטויי חוק מוזנים בשדה החוקים למטה.'));
        }
        return Promise.resolve();
    };
    // When the user edits any row, clear server errors so the red
    // highlight disappears as they type.
    const onRowsChange = (next) => {
        setRows(next);
        if (Object.keys(serverErrors).length > 0)
            setServerErrors({});
    };
    const formKey = isEdit ? `edit-${initial.id}` : `create-${lastSavedId ?? 'new'}`;
    return (_jsxs(Modal, { title: isEdit ? `עריכת סט: ${initial.name}` : 'סט חוקים חדש', open: open, onCancel: onClose, onOk: submit, okText: "\u05E9\u05DE\u05D5\u05E8", cancelText: "\u05D1\u05D8\u05DC", confirmLoading: submitting, destroyOnHidden: true, width: 720, children: [isEdit && (_jsx(Alert, { type: "info", showIcon: true, message: "\u05E9\u05D9\u05DE\u05D5 \u05DC\u05D1: \u05E2\u05E8\u05D9\u05DB\u05EA \u05D4\u05E1\u05D8 \u05DE\u05E9\u05E4\u05D9\u05E2\u05D4 \u05E2\u05DC \u05DB\u05DC \u05D4\u05DE\u05E7\u05D5\u05DE\u05D5\u05EA \u05E9\u05D1\u05D4\u05DD \u05D4\u05D5\u05D0 \u05D1\u05E9\u05D9\u05DE\u05D5\u05E9, \u05D2\u05DD \u05D1\u05D1\u05D7\u05E0\u05D9\u05DD \u05D0\u05D7\u05E8\u05D9\u05DD.", style: { marginBottom: 16 } })), _jsxs(Form, { form: form, layout: "vertical", preserve: false, initialValues: initialValues, children: [_jsx(Form.Item, { name: "name", label: "\u05E9\u05DD \u05D4\u05E1\u05D8", rules: [
                            { required: true, message: 'שם נדרש' },
                            { validator: nameLooksLikeRule },
                        ], tooltip: "\u05EA\u05D5\u05D5\u05D9\u05EA \u05E7\u05E6\u05E8\u05D4, \u05DC\u05DE\u05E9\u05DC: \u05D1\u05E1\u05D9\u05E1\u05D9 / \u05DE\u05EA\u05E7\u05D3\u05DD / \u05E9\u05DC\u05E9\u05D5\u05EA \u05E4\u05D9\u05EA\u05D2\u05D5\u05E8\u05D9\u05D5\u05EA", children: _jsx(Input, { placeholder: "\u05DC\u05DE\u05E9\u05DC: \u05D1\u05E1\u05D9\u05E1\u05D9" }) }), _jsxs("div", { style: { marginBottom: 16 }, children: [_jsx(Text, { style: { fontWeight: 500, display: 'block', marginBottom: 8 }, children: "\u05D7\u05D5\u05E7\u05D9\u05DD" }), _jsx(Text, { type: "secondary", style: { fontSize: 12, display: 'block', marginBottom: 8 }, children: "\u05DB\u05DC \u05D7\u05D5\u05E7 \u05D4\u05D5\u05D0 \u05EA\u05E0\u05D0\u05D9 \u05E0\u05E4\u05E8\u05D3. \u05D4\u05EA\u05E0\u05D0\u05D9\u05DD \u05DE\u05E6\u05D8\u05E8\u05E4\u05D9\u05DD \u05D6\u05D4 \u05DC\u05D6\u05D4 (\u05D5\u05D2\u05DD)." }), _jsx(RuleBuilder, { rows: rows, onChange: onRowsChange, variables: variables, serverErrors: serverErrors })] }), _jsx(Form.Item, { name: "is_default", valuePropName: "checked", tooltip: "\u05E1\u05D9\u05DE\u05D5\u05DF \u05D6\u05D4 \u05E7\u05D5\u05D1\u05E2 \u05E9\u05D4\u05E1\u05D8 \u05D9\u05D9\u05D1\u05D7\u05E8 \u05D0\u05D5\u05D8\u05D5\u05DE\u05D8\u05D9\u05EA \u05DB\u05E9\u05DE\u05E6\u05DE\u05D9\u05D3\u05D9\u05DD \u05D0\u05EA \u05D4\u05EA\u05D1\u05E0\u05D9\u05EA \u05DC\u05D1\u05D5\u05D7\u05DF. \u05D0\u05D9\u05DF \u05DC\u05DB\u05DA \u05D4\u05E9\u05E4\u05E2\u05D4 \u05E2\u05DC \u05D4\u05D4\u05EA\u05E0\u05D4\u05D2\u05D5\u05EA \u05D1\u05D1\u05D5\u05D7\u05DF \u05E2\u05E6\u05DE\u05D5.", children: _jsx(Checkbox, { children: "\u05E1\u05D8 \u05D1\u05E8\u05D9\u05E8\u05EA \u05D4\u05DE\u05D7\u05D3\u05DC \u05E9\u05DC \u05D4\u05EA\u05D1\u05E0\u05D9\u05EA" }) })] }, formKey), _jsxs(Modal, { open: showUnsatisfiableWarning, title: "\u05D9\u05D9\u05EA\u05DB\u05DF \u05E9\u05D4\u05E1\u05D8 \u05D1\u05DC\u05EA\u05D9-\u05D0\u05E4\u05E9\u05E8\u05D9", okText: "\u05E9\u05DE\u05D5\u05E8 \u05D1\u05DB\u05DC \u05D6\u05D0\u05EA", cancelText: "\u05D7\u05D6\u05D5\u05E8 \u05DC\u05E2\u05E8\u05D9\u05DB\u05D4", okButtonProps: { danger: true }, onOk: confirmForceSave, onCancel: () => setShowUnsatisfiableWarning(false), confirmLoading: submitting, children: [_jsx("p", { children: "\u05DC\u05D0 \u05E0\u05DE\u05E6\u05D0\u05D5 \u05E2\u05E8\u05DB\u05D9\u05DD \u05D4\u05E2\u05D5\u05DE\u05D3\u05D9\u05DD \u05D1\u05D7\u05D5\u05E7\u05D9\u05DD \u05D1\u05D3\u05D2\u05D9\u05DE\u05D4." }), _jsx("p", { children: "\u05D9\u05D9\u05EA\u05DB\u05DF \u05E9\u05D4\u05E1\u05D8 \u05D1\u05DC\u05EA\u05D9-\u05D0\u05E4\u05E9\u05E8\u05D9 (\u05DB\u05DE\u05D5 a > b \u05D9\u05D7\u05D3 \u05E2\u05DD b > a), \u05D0\u05D5 \u05E9\u05D4\u05E1\u05D1\u05D9\u05E8\u05D5\u05EA \u05DC\u05DE\u05E6\u05D5\u05D0 \u05E2\u05E8\u05DA \u05EA\u05E7\u05E3 \u05E0\u05DE\u05D5\u05DB\u05D4 \u05DE\u05D3\u05D9 \u05D1\u05D8\u05D5\u05D5\u05D7 \u05D4\u05D3\u05D2\u05D9\u05DE\u05D4." }), _jsx("p", { children: "\u05DC\u05E9\u05DE\u05D5\u05E8 \u05D1\u05DB\u05DC \u05D6\u05D0\u05EA?" })] })] }));
}
